/**
 * Wind (AD-007k), ported from the wind model recovered out of `RB.EXE` itself.
 *
 * The four `.WND` files are byte-identical stubs and carry no wind at all, so
 * the question "where does the original get wind?" was open. It is answered in
 * the executable: a **74-byte wind record compiled into `DGROUP` at `ds:0x1368`**,
 * holding three altitude-band boundaries followed by one 3-component velocity
 * per band. The full evidence chain — the loader, the selector, the realism
 * gate and the single place the flight model consumes it — is in
 * `docs/engineering/flight-model-design.md` AD-007k.
 *
 * Three properties of the original's model matter here, and this module keeps
 * all three:
 *
 * 1. **Wind is a world-frame velocity added to the flight path, nothing more.**
 *    The original adds the band's vector to the object's velocity *inside the
 *    integrator's local*, leaving the object's own stored velocity untouched.
 *    So wind moves the aeroplane over the ground and does not change its
 *    airspeed, its angle of attack, its trim or its stall margin — which is
 *    also the physically right answer, because wind moves the air mass the
 *    aeroplane is already flying in.
 * 2. **It is banded by altitude, not continuous.** `windBandIndex` is a direct
 *    port of the original's loop: walk the three boundaries and stop at the
 *    first one the aircraft is at or below, giving 0…3.
 * 3. **It is zero on the ground.** The original tests the object's ground flag
 *    and substitutes a zero vector, so a parked or rolling aeroplane is never
 *    pushed sideways.
 *
 * There are no gusts. The compiled record has a three-word field that looks
 * like it was meant for them, but nothing in the executable reads it, and no
 * random stream is touched anywhere on the wind path — so this module is a pure
 * function of altitude and configuration, and adds no state and no RNG draw.
 */

import { type Vec3 } from "./math3d.ts";
import type { Tunables } from "./tunables.ts";
import { metresToFeet } from "./units.ts";

/**
 * The weather severities, in the original's own order — `RB.EXE`'s string table
 * at `ds:0x0614` reads `still`, `light`, `strong`, `stormy`, and the scenario
 * header's weather byte indexes it to build `<name>.WND`. VERIFIED.
 */
export const WEATHER_KINDS = ["still", "light", "strong", "stormy"] as const;

export type WeatherKind = (typeof WEATHER_KINDS)[number];

/**
 * The three altitude-band boundaries of the compiled-in wind record, in feet.
 * VERIFIED: `{500, 50000, 90000}` at `ds:0x1368`. Only the first boundary is
 * inside a WWI aeroplane's world — every aircraft in the fleet spends its whole
 * life in band 1 once it is more than 500 ft up, and bands 2 and 3 are
 * unreachable. They are kept because they are what the original carries.
 */
export const WIND_BAND_CEILINGS_FT: readonly number[] = [500, 50000, 90000];

/**
 * The per-band wind magnitude in the original's own velocity unit, VERIFIED off
 * the same record: `{10, 20, 40, 60}`, one per band including the "above every
 * boundary" band 3. Used as a *ratio* — `windReferenceMps` fixes the SI scale of
 * band 1 and the others follow this profile — because the unit itself is not
 * recoverable from the data.
 */
export const WIND_BAND_PROFILE: readonly number[] = [10, 20, 40, 60];

/** The band whose speed `windReferenceMps` names: the one the flight envelope actually occupies. */
export const WIND_REFERENCE_BAND = 1;

/**
 * Per-severity scale on the wind speed — what the briefed word is worth
 * (AD-007k §10.2).
 *
 * The original cannot answer this from its `.WND` files: it picks one per
 * severity and all four are byte-identical, so in the shipped game the word
 * moves the cloud deck (`clouds.c` reads the same weather byte) and nothing
 * else. With the wind live that would make the briefing a lie — a sortie told
 * *"the winds are still"* flying the same 13 mph as one told *"strong"* — so
 * the word is given the meaning the executable's own record implies.
 *
 * **`still` is 0, because "still" means still.** The other three come off the
 * one place in the wind record that carries a *three-wide* table: the unread
 * i16 triple at `+0x2c`, `{10, 15, 20}`, VERIFIED in `RB.EXE` alongside every
 * other field of the record. It sits in the wind struct, in the wind slots' own
 * unit, and there are exactly three of it against four severity names — one per
 * severity that has any weather in it. Read that way the ratios are
 * 10 : 15 : 20 = **1 : 1.5 : 2**, `light` on the reference and `strong` half as
 * much again, which is also the ratio a plain reading of the words would pick.
 *
 * Stated honestly: this build never reads that triple (AD-007k §2 lists its five
 * reference sites, and none of them touch it), so it is a *shape* taken from the
 * data rather than a decoded severity table — the same standing as
 * `WIND_BAND_PROFILE`, which is likewise used as a ratio because its unit is not
 * recoverable. What it is not is a guess: the alternative shapes in the record
 * are the four-wide band array, which is an altitude profile and answers a
 * different question, and `+0x10`'s lone `60`, which is band 3's own speed.
 *
 * `stormy` is here because the record's third entry is, not because anything
 * draws it: `mission-world.ts`'s `WIND_STRENGTHS` is `PS.EXE`'s three words, and
 * the fourth name in `RB.EXE`'s table stays unexposed exactly as the original
 * leaves it.
 */
export const WEATHER_WIND_SCALE: Readonly<Record<WeatherKind, number>> = {
  still: 0,
  light: 1,
  strong: 1.5,
  stormy: 2,
};

export const ZERO_WIND: Vec3 = { x: 0, y: 0, z: 0 };

/**
 * A direct port of the original's band search: return the index of the first
 * boundary the aircraft is at or below, or `WIND_BAND_CEILINGS_FT.length` when
 * it is above all of them. Altitude is above datum, in feet, matching the unit
 * the compiled boundaries are written in.
 */
export function windBandIndex(altitudeFt: number): number {
  for (let band = 0; band < WIND_BAND_CEILINGS_FT.length; band += 1) {
    if (altitudeFt <= WIND_BAND_CEILINGS_FT[band]!) return band;
  }
  return WIND_BAND_CEILINGS_FT.length;
}

/** Wind speed in m/s for one band, scaling the VERIFIED profile onto the SI reference. */
export function windSpeedMps(band: number, weather: WeatherKind, tunables: Tunables): number {
  const profile = WIND_BAND_PROFILE[band] ?? WIND_BAND_PROFILE[WIND_BAND_PROFILE.length - 1]!;
  const reference = WIND_BAND_PROFILE[WIND_REFERENCE_BAND]!;
  return (tunables.windReferenceMps * profile * WEATHER_WIND_SCALE[weather]) / reference;
}

export interface WindQuery {
  /** Altitude above datum, metres — the sim's own `position.y`. */
  readonly altitudeM: number;
  /** True when the wheels are on the surface; the original substitutes zero wind there. */
  readonly grounded: boolean;
  /** The resolved REALISTIC WEATHER subsystem flag. Off means no wind at all. */
  readonly enabled: boolean;
  readonly weather: WeatherKind;
  readonly tunables: Tunables;
}

/**
 * The world-frame wind velocity to add to the flight path this tick. Horizontal
 * by construction: the original's per-band vector has one non-zero component and
 * no vertical term, so there is no updraught or downdraught in this model.
 */
export function windVelocity(query: WindQuery): Vec3 {
  if (!query.enabled || query.grounded) return ZERO_WIND;
  const band = windBandIndex(metresToFeet(query.altitudeM));
  const speed = windSpeedMps(band, query.weather, query.tunables);
  if (speed === 0) return ZERO_WIND;
  // `math3d.ts`'s heading convention: 0 = −Z (north), increasing clockwise from
  // above, so a heading θ points at (sin θ, 0, −cos θ). π/2 is +X, due east.
  const heading = query.tunables.windDirectionRad;
  return { x: Math.sin(heading) * speed, y: 0, z: -Math.cos(heading) * speed };
}
